import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  normalizeCombinedSearchInput,
  getConversationTopicsSocketPayload,
  resolveSearchQueryForApi,
} from "../utils/phoneSearch";
import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Pagination,
  Stack,
  TextField,
  InputAdornment,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useChatContext } from "../hooks/ChatProvider";
import dayjs from "dayjs";
import { getTenantTimezone } from "../utils/dateUtils";
import { WS_BASE_URL } from "../config/environment";

// ---------- helpers ----------
const formatTimestamp = (ts) => {
  if (!ts) return "";
  const tz = getTenantTimezone();
  const d = dayjs(ts).tz(tz);
  const now = dayjs().tz(tz);
  const isToday = d.isSame(now, "day");
  if (isToday) {
    return d.format("hh:mm A");
  }
  return d.format("MMM D");
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

// Deterministic avatar colour per name
const AVATAR_COLORS = [
  "#4f86c6",
  "#6a9e73",
  "#c47d3e",
  "#8e6bbf",
  "#c4515b",
  "#4aada8",
  "#b56e8a",
  "#5b8db8",
];
const avatarColor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

// ---------- skeleton ----------
const ChatListSkeleton = () => (
  <List disablePadding>
    {Array.from({ length: 7 }).map((_, i) => (
      <React.Fragment key={i}>
        <ListItem sx={{ px: 2, py: 1.25, alignItems: "center" }}>
          <Box sx={{ mr: 1.5, flexShrink: 0 }}>
            <Skeleton variant="circular" width={40} height={40} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" justifyContent="space-between" mb={0.6}>
              <Skeleton variant="text" width="55%" height={14} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" width="18%" height={12} sx={{ borderRadius: 1 }} />
            </Box>
            <Skeleton variant="text" width="40%" height={12} sx={{ borderRadius: 1 }} />
          </Box>
        </ListItem>
        {i < 6 && <Box sx={{ mx: 2, borderBottom: "1px solid #f3f3f3" }} />}
      </React.Fragment>
    ))}
  </List>
);

// ---------- component ----------
// channel: "chats" | "calls" | "all" — drives the Chat/Calls/All tabs.
// This is the single listing component for all three: the backend
// (chat/consumers.py's SupportChatQueueConsumer) treats every contact as
// one Topic row regardless of which channel(s) they've used, so switching
// channel here is just a different filter/sort over the same live
// WebSocket connection, not a different data source or a reconnect.
const ChatList = ({ setIsChatSelected, channel = "chats" }) => {
  const [chats, setChats] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputError, setSearchInputError] = useState(null);
  const [hasMoreTopics, setHasMoreTopics] = useState(false);
  const [topicsPage, setTopicsPage] = useState(1);
  const socketRef = useRef(null);
  const currentPageRef = useRef(1);
  const isPopStateRef = useRef(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [jumpValue, setJumpValue] = useState("");
  const pageSize = 20;

  const totalPages = useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount, pageSize],
  );
  const { selectedUser, setSelectedUser } = useChatContext();

  const resolvedChatListSearch = useMemo(
    () => resolveSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  // Search effect
  useEffect(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      const { blockSend, query } = getConversationTopicsSocketPayload(searchQuery);
      if (blockSend) return;
      socketRef.current.send(
        JSON.stringify({
          type: "topics.load_page",
          page: 1,
          query,
          channel,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Channel (tab) switch effect — same socket, just a fresh page 1 request
  // with the new filter. On the very first mount the socket is normally
  // still CONNECTING (not yet OPEN) by the time this runs, so the guard
  // below naturally skips a redundant request on top of connect()'s own
  // topics.init — same reasoning the search effect above already relies on.
  useEffect(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      const { blockSend, query } = getConversationTopicsSocketPayload(searchQuery);
      socketRef.current.send(
        JSON.stringify({
          type: "topics.load_page",
          page: 1,
          query: blockSend ? "" : query,
          channel,
        }),
      );
      setCurrentPage(1);
      setLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  const handleClick = (user) => {
    setSelectedUser(user);
    localStorage.setItem("lastTopicSelected", JSON.stringify(user.topic));
    setIsChatSelected(true);
  };

  const handlePageChange = (_, value) => {
    if (value !== currentPage) loadMoreTopics(value);
  };

  useEffect(() => {
    currentPageRef.current = currentPage;
    if (isPopStateRef.current) {
      isPopStateRef.current = false;
      return;
    }
  
    if (currentPage > 1) {
      window.history.pushState(
        { chatListPage: currentPage },
        "",
        // window.location.href,
      );
    }
  }, [currentPage]);
  
  useEffect(() => {
    const handlePopState = (event) => {
      const page = event.state?.chatListPage ?? 1;
  
      if (page === currentPageRef.current) {
        return;
      }
  
      isPopStateRef.current = true;
  
      const { blockSend: searchBlocked, query: currentQuery } =
        getConversationTopicsSocketPayload(searchQueryRef.current);
      socketRef.current?.send(
        JSON.stringify({
          type: "topics.load_page",
          page,
          query: searchBlocked ? "" : currentQuery,
          channel: channelRef.current,
        }),
      );
    };
  
    window.addEventListener("popstate", handlePopState);
  
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    const normalized = normalizeCombinedSearchInput(value);
    const trimmed = (normalized ?? "").trim();
    const plusCount = (trimmed.match(/\+/g) || []).length;
    const misplacedPlus = plusCount > 1 || (plusCount === 1 && !trimmed.startsWith("+"));

    if (misplacedPlus) {
      setSearchInputError("'+' is only allowed at the beginning.");
    } else {
      setSearchInputError(null);
    }

    setSearchQuery(normalized);
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== "+") return;

    const value = e.target.value || "";
    const selectionStart = e.target.selectionStart ?? 0;
    const plusCount = (value.match(/\+/g) || []).length;
    const willInsertAtStart = selectionStart === 0;
    const invalidPlus = plusCount > 0 || !willInsertAtStart;

    if (invalidPlus) {
      e.preventDefault();
      setSearchInputError("'+' is only allowed at the beginning.");
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const adminSubscriberCode = localStorage.getItem("code");

  // Use a ref to track selectedUser inside the socket handler
  // so we never need selectedUser in the useEffect dependency array.
  // This prevents the socket from restarting (and pagination from resetting)
  // every time the user clicks a contact.
  const selectedUserRef = useRef(selectedUser);
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  const searchQueryRef = useRef(searchQuery);
  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  const channelRef = useRef(channel);
  useEffect(() => {
    channelRef.current = channel;
  }, [channel]);

  const sortByLatest = (arr) =>
    [...arr].sort((a, b) => {
      if (a.is_active !== b.is_active) {
        return a.is_active ? -1 : 1;
      }
      if (a.last_message_timestamp && b.last_message_timestamp)
        return (
          new Date(b.last_message_timestamp) -
          new Date(a.last_message_timestamp)
        );
      return 0;
    });

  useEffect(() => {
    const handler = (e) => {
      const endedTopic = e.detail?.endedTopic;
      if (!endedTopic) return;
      setChats((prev) => {
        const sorted = [...prev]
          .map((c) =>
            c.topic === endedTopic ? { ...c, is_active: false } : c,
          )
          .sort((a, b) => {
            if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
            if (a.last_message_timestamp && b.last_message_timestamp)
              return (
                new Date(b.last_message_timestamp) -
                new Date(a.last_message_timestamp)
              );
            return 0;
          });
        const firstUser = sorted[0] ?? null;
        setSelectedUser(firstUser);
        setIsChatSelected(Boolean(firstUser));
        return sorted;
      });
    };
    window.addEventListener("bb-after-end-chat", handler);
    return () => window.removeEventListener("bb-after-end-chat", handler);
  }, [setChats, setSelectedUser, setIsChatSelected]);

  useEffect(() => {
    const tenant = localStorage.getItem("tenant");
    // channel here only seeds the FIRST topics.init — intentionally not a
    // dependency of this effect (see its own eslint-disable below): later
    // channel changes go over this same connection via the [channel]
    // effect above, not a reconnect, so the socket must stay tied to
    // adminSubscriberCode alone.
    // A login without a chat Subscriber gets an empty code in its token, and
    // the server has no route for that URL — fail fast instead of opening a
    // socket that can never deliver topics.init.
    if (!adminSubscriberCode || adminSubscriberCode === "undefined") {
      setLoading(false);
      setConnectionError(true);
      return undefined;
    }
    setConnectionError(false);

    const socketURL = `${WS_BASE_URL}/ws/chat/list/admin/${adminSubscriberCode}/?tenant=${tenant}&channel=${channel}`;

    const socket = new WebSocket(socketURL);
    socketRef.current = socket;
    let initReceived = false;

    // Closed (rejected or dropped) before the first page arrived: stop the
    // skeleton and show an error instead of loading forever.
    socket.onclose = () => {
      if (!initReceived) {
        setLoading(false);
        setConnectionError(true);
      }
    };

    socket.onmessage = (e) => {
      const data = JSON.parse(e.data);
      const { notification_type, tenant_subdomain } = data;
      const tenant = localStorage.getItem("tenant");

      if (notification_type === "topics.init") {
        initReceived = true;
        const incoming = data.topics.map((t) => ({
          id: t.topic,
          topic: t.topic,
          phone: t.publisher?.phone,
          name: t.publisher?.name,
          message: t.last_msg?.message,
          last_message_timestamp: t.last_msg?.timestamp,
          supportUser: t.support_subscriber,
          tenant_subdomain: t.tenant_subdomain,
          is_active: t.is_active,
          unread_message_count: t.unread_message_count,
          has_chat: Boolean(t.has_chat),
          has_call: Boolean(t.has_call),
        }));
        const sorted = sortByLatest(incoming);
        setChats(sorted);
        setHasMoreTopics(data.has_more);
        setTopicsPage(data.page);
        setCurrentPage(data.page);
        setTotalCount(data.total_count);
        setLoading(false);
        if (!selectedUserRef.current && sorted.length > 0) {
          setSelectedUser(sorted[0]);
          localStorage.setItem(
            "lastTopicSelected",
            JSON.stringify(sorted[0].topic),
          );
        }
        return;
      }

      if (notification_type === "topics.page") {
        const incoming = data.topics.map((t) => ({
          id: t.topic,
          topic: t.topic,
          phone: t.publisher?.phone,
          name: t.publisher?.name,
          message: t.last_msg?.message,
          last_message_timestamp: t.last_msg?.timestamp,
          supportUser: t.support_subscriber,
          tenant_subdomain: t.tenant_subdomain,
          is_active: t.is_active,
          unread_message_count: t.unread_message_count,
          has_chat: Boolean(t.has_chat),
          has_call: Boolean(t.has_call),
        }));
        setChats(sortByLatest(incoming));
        setHasMoreTopics(data.has_more);
        setTopicsPage(data.page);
        setCurrentPage(data.page);
        setTotalCount(data.total_count);
        setLoading(false);
        return;
      }

      const { topic, publisher, last_msg, support_subscriber, unread_message_count } = data;
      const { message, timestamp } = last_msg || {};
      const { phone, name } = publisher || {};

      setChats((prevChats) => {
        let updatedChats = [...prevChats];
        let selectedUserChanged = false;
        const currentSelected = selectedUserRef.current;

        if (notification_type === "topics.remove") {
          updatedChats = updatedChats.filter((chat) => {
            if (chat.topic === topic && currentSelected?.topic === topic)
              selectedUserChanged = true;
            return true;
          });
          
          // Set chat is inactive
          updatedChats = updatedChats.map((chat) => {
            if (chat.topic === topic) {
              return {
                ...chat,
                is_active: false,
              };
            }
            return chat;
          });

        } else if (
          notification_type === "topics.update" ||
          notification_type === undefined
        ) {
          const idx = updatedChats.findIndex((c) => c.topic === topic);
          if (idx !== -1) {
            updatedChats[idx] = {
              ...updatedChats[idx],
              message,
              last_message_timestamp: timestamp,
              topic,
              supportUser: support_subscriber,
              unread_message_count: unread_message_count,
              ...(name && { name }),
              tenant_subdomain,
              ...(data.is_active !== undefined && {
                is_active: data.is_active,
              }),
              // A live topics.update always means a message was just
              // exchanged, regardless of which channel is active — this
              // row already passed the server's channel filter when it
              // was first loaded (see the idx === -1 branch below for why
              // that's not true of a brand-new row), so it's always safe
              // to mark it here.
              has_chat: true,
            };
            if (currentSelected?.topic === topic) {
              setSelectedUser({
                ...currentSelected,
                ...updatedChats[idx],
              });
            }
          } else {
            // A contact not yet in the currently-loaded list. This event
            // only tells us a chat message happened — it carries no
            // has_call info — so on the Calls-only tab we can't tell
            // whether they actually belong here; skip adding rather than
            // risk showing a chat-only contact under Calls. Chats/All
            // have no such ambiguity: a brand-new message always belongs
            // there.
            if (tenant_subdomain === tenant && channelRef.current !== "calls")
              updatedChats.push({
                id: topic,
                topic,
                phone,
                name,
                message,
                last_message_timestamp: timestamp,
                supportUser: support_subscriber,
                is_active: data.is_active,
                has_chat: true,
                has_call: false,
              });
          }
        } else if (notification_type === "topics.update.support_subscriber") {
          const idx = updatedChats.findIndex((c) => c.topic === topic);
          if (idx !== -1 && tenant_subdomain === tenant) {
            updatedChats[idx] = {
              ...updatedChats[idx],
              supportUser: support_subscriber,
              is_active: true,
              unread_message_count: 0, // Reset unread count when support takes over
              ...(name && { name }),
              tenant_subdomain,
            };
            if (currentSelected?.topic === topic)
              setSelectedUser({
                ...currentSelected,
                supportUser: support_subscriber,
                is_active: true,
                unread_message_count: 0, // Reset unread count when support takes over
                ...(name && { name }),
              });
          } else if (idx === -1 && tenant_subdomain === tenant) {
            updatedChats.push({
              id: topic,
              topic,
              phone,
              name,
              message,
              last_message_timestamp: timestamp,
              supportUser: support_subscriber,
              is_active: true,
              unread_message_count: 0,
              tenant_subdomain,
            });
          }
        }

        if (selectedUserChanged || !currentSelected) {
          const nextUser = updatedChats[0] || null;
          setSelectedUser(nextUser);
          if (nextUser)
            localStorage.setItem(
              "lastTopicSelected",
              JSON.stringify(nextUser.topic),
            );
          else localStorage.removeItem("lastTopicSelected");
        }

        return sortByLatest(updatedChats);
      });
    };

    return () => {
      // Our own close (unmount / re-login) is not a connection failure.
      socket.onclose = null;
      socket.close();
    };
    // Only restart socket if adminSubscriberCode changes (e.g. re-login).
    // Intentionally excluding selectedUser — handled via selectedUserRef
    // above — and channel, which only seeds the initial connection URL;
    // see this effect's own comment above for why.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSubscriberCode]);

  const loadMoreTopics = (page) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      const { blockSend, query } = getConversationTopicsSocketPayload(searchQuery);
      socketRef.current.send(
        JSON.stringify({ type: "topics.load_page", page, query: blockSend ? "" : query, channel }),
      );
    }
  };

  // ---------- render ----------
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        bgcolor: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Search Bar */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0" }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search by name or phone..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          error={Boolean(searchInputError) || Boolean(resolvedChatListSearch.blockFetch && resolvedChatListSearch.phoneSearchError)}
          helperText={searchInputError || (resolvedChatListSearch.blockFetch ? resolvedChatListSearch.phoneSearchError : undefined)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9e9e9e", fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch} edge="end">
                  <ClearIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#f5f7fa",
              fontSize: "0.85rem",
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: "#d0d0d0" },
              "&.Mui-focused fieldset": {
                borderColor: "#1976d2",
                borderWidth: 1,
              },
            },
            "& input": { color: "#333", py: "8px" },
            "& input::placeholder": { color: "#aaa", opacity: 1 },
          }}
        />
      </Box>

      {/* Count Row */}
      {chats.length > 0 && (
        <Box sx={{ px: 2, py: "6px", borderBottom: "1px solid #f0f0f0" }}>
          <Typography
            sx={{ fontSize: "0.72rem", color: "#9e9e9e", fontWeight: 500 }}
          >
            {totalCount} chat{totalCount !== 1 ? "s" : ""}
            {searchQuery ? " (filtered)" : ""} &bull; Page {currentPage} of{" "}
            {totalPages || 1}
          </Typography>
        </Box>
      )}

      {/* List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "#e0e0e0 transparent",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: "#e0e0e0",
            borderRadius: 4,
          },
        }}
      >
        {loading ? (
          <ChatListSkeleton />
        ) : connectionError ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={6}
            px={2}
          >
            <ErrorOutlineIcon sx={{ fontSize: 40, color: "#e57373", mb: 1.5 }} />
            <Typography variant="body2" fontWeight={600} color="#555" mb={0.5}>
              Couldn't load conversations
            </Typography>
            <Typography variant="caption" color="#aaa" textAlign="center">
              Please log out and log in again. If this keeps happening,
              contact support.
            </Typography>
          </Box>
        ) : chats.length === 0 && searchQuery ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={6}
            px={2}
          >
            <SearchIcon sx={{ fontSize: 40, color: "#ccc", mb: 1.5 }} />
            <Typography variant="body2" fontWeight={600} color="#555" mb={0.5}>
              No results found
            </Typography>
            <Typography variant="caption" color="#aaa" textAlign="center">
              No chats match "{searchQuery}". Try a different term.
            </Typography>
          </Box>
        ) : chats.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={6}
            px={2}
          >
            <ChatBubbleOutlineIcon
              sx={{ fontSize: 40, color: "#ccc", mb: 1.5 }}
            />
            <Typography variant="body2" fontWeight={600} color="#555" mb={0.5}>
              No chats yet
            </Typography>
            <Typography variant="caption" color="#aaa" textAlign="center">
              Start a new conversation to see chats here.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {chats.map((chat, index) => {
              const isSelected = selectedUser?.topic === chat.topic;
              const unreadCount = chat.unread_message_count;
              const color = avatarColor(chat.name || chat.phone);
              return (
                <React.Fragment key={chat.id}>
                  <ListItem
                    button
                    onClick={() => handleClick(chat)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      position: "relative",
                      bgcolor: isSelected ? "#dbeafe" : "transparent",
                      borderLeft: isSelected
                        ? "4px solid #1565c0"
                        : "4px solid transparent",
                      boxShadow: isSelected
                        ? "inset 0 0 0 1px rgba(21,101,192,0.12)"
                        : "none",
                      transition: "background 0.15s ease, box-shadow 0.15s ease",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: isSelected ? "#dbeafe" : "#f0f4ff",
                        "& .chat-name": { color: isSelected ? "#1565c0" : "#1976d2" },
                      },
                      alignItems: "center",
                    }}
                  >
                    {/* Avatar */}
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: color,
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          letterSpacing: 0.5,
                        }}
                      >
                        {getInitials(chat.name)}
                      </Avatar>
                    </ListItemAvatar>

                    {/* Text Block */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={0.2}
                      >
                        <Typography
                          noWrap
                          className="chat-name"
                          sx={{
                            fontWeight: isSelected ? 700 : 600,
                            fontSize: "0.87rem",
                            color: isSelected ? "#1565c0" : "#222",
                            maxWidth: "70%",
                            transition: "color 0.15s ease",
                          }}
                        >
                          {chat.name || "Unknown"}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {/* <Box
                            sx={{
                              px: 0.75,
                              py: 0.15,
                              borderRadius: "6px",
                              bgcolor: chat.is_active ? "#e8f5e9" : "#f5f5f5",
                              border: `1px solid ${chat.is_active ? "#a5d6a7" : "#e0e0e0"}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.58rem",
                                fontWeight: 700,
                                color: chat.is_active ? "#2e7d32" : "#9e9e9e",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                lineHeight: 1.4,
                              }}
                            >
                              {chat.is_active ? "● Live" : "History"}
                            </Typography>
                          </Box> */}
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#aaa",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatTimestamp(chat.last_message_timestamp)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box display="flex" alignItems="center" gap={0.6} minWidth={0}>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.75rem",
                              color: "#888",
                              maxWidth: unreadCount > 0 ? "75%" : "100%",
                            }}
                          >
                            +{chat.phone}
                          </Typography>
                          {channel === "all" && (
                            <Box display="flex" alignItems="center" gap={0.4} flexShrink={0}>
                              {chat.has_chat && (
                                <Tooltip title="Has chat history">
                                  <ChatBubbleOutlineIcon sx={{ fontSize: 12, color: "#1976d2" }} />
                                </Tooltip>
                              )}
                              {chat.has_call && (
                                <Tooltip title="Has call history">
                                  <PhoneIcon sx={{ fontSize: 12, color: "#2e7d32" }} />
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </Box>
                        {unreadCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 18,
                              height: 18,
                              px: 0.5,
                              borderRadius: "9px",
                              bgcolor: "#4caf50",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#fff",
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {unreadCount > 99 ? "99+" : unreadCount}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItem>

                  {index < chats.length - 1 && (
                    <Box sx={{ mx: 2, borderBottom: "1px solid #f3f3f3" }} />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            borderTop: "1px solid #f0f0f0",
            px: 1.5,
            py: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: "0.72rem",
                minWidth: 28,
                height: 28,
                borderRadius: "48px",
                transition: "background 0.15s ease, color 0.15s ease, transform 0.1s ease",
                "&:hover": {
                  bgcolor: "#e3eefb",
                  color: "#1565c0",
                  transform: "scale(1.08)",
                },
                "&.Mui-selected": {
                  bgcolor: "#1565c0",
                  color: "#fff",
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: "#1044a0",
                    transform: "scale(1.08)",
                  },
                },
                "&.MuiPaginationItem-previousNext": {
                  "&:hover": { bgcolor: "#e3eefb", color: "#1565c0" },
                },
                "&.MuiPaginationItem-ellipsis": {
                  "&:hover": { bgcolor: "transparent", transform: "none" },
                },
              },
            }}
          />
          {/* Direct page jump */}
          <Box display="flex" alignItems="center" gap={0.75}>
            <Typography sx={{ fontSize: "0.68rem", color: "#aaa" }}>
              Go to page
            </Typography>
            <TextField
              size="small"
              value={jumpValue}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                setJumpValue(v);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const p = Math.min(Math.max(parseInt(jumpValue, 10), 1), totalPages);
                  if (!isNaN(p) && p !== currentPage) handlePageChange(null, p);
                  setJumpValue("");
                }
              }}
              inputProps={{ style: { textAlign: "center", padding: "2px 4px", fontSize: "0.72rem" } }}
              sx={{
                width: 42,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  "& fieldset": { borderColor: "#d0d0d0" },
                  "&:hover fieldset": { borderColor: "#1976d2" },
                  "&.Mui-focused fieldset": { borderColor: "#1565c0", borderWidth: 1 },
                },
              }}
            />
            <Tooltip title={`Jump to page (1–${totalPages})`} placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  const p = Math.min(Math.max(parseInt(jumpValue, 10), 1), totalPages);
                  if (!isNaN(p) && p !== currentPage) handlePageChange(null, p);
                  setJumpValue("");
                }}
                sx={{
                  width: 22,
                  height: 22,
                  bgcolor: "#1565c0",
                  color: "#fff",
                  borderRadius: "6px",
                  "&:hover": { bgcolor: "#1044a0" },
                }}
              >
                <SubdirectoryArrowRightIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ChatList;
